import React from 'react';

/**
 * Two-column layout for long-form legal docs (Privacy, Terms).
 * Left: sticky TOC with anchor links to each section.
 * Right: rendered sections.
 *
 * Usage: pass `sections` as an array of { id, title, body } where body is JSX.
 */
export default function LegalLayout({ title, eyebrow, lastUpdated, intro, sections }) {
  return (
    <>
      {/* Title block */}
      <section className="oc-section oc-section--white" style={{ paddingTop: 64, paddingBottom: 40 }}>
        <div className="oc-shell">
          <div className="oc-eyebrow oc-mb-2">{eyebrow}</div>
          <h1 className="oc-h1" style={{ fontSize: 44 }}>{title}</h1>
          {lastUpdated && (
            <div className="oc-label oc-mt-3">Last updated · {lastUpdated}</div>
          )}
          {intro && (
            <p className="oc-body oc-mt-4 oc-max-680" style={{ fontSize: 16 }}>
              {intro}
            </p>
          )}
        </div>
      </section>

      {/* Two-column body */}
      <section className="oc-section" style={{ paddingTop: 56 }}>
        <div className="oc-shell">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '220px 1fr',
              gap: 64,
              alignItems: 'start',
            }}
            className="oc-legal-grid"
          >
            {/* TOC */}
            <nav
              aria-label="Section navigation"
              style={{ position: 'sticky', top: 96 }}
              className="oc-legal-toc"
            >
              <div className="oc-eyebrow-gray oc-mb-3">Contents</div>
              <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      style={{
                        display: 'flex',
                        gap: 10,
                        fontSize: 13,
                        lineHeight: 1.45,
                        color: 'var(--oc-gray)',
                        textDecoration: 'none',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'var(--oc-font-mono)',
                          fontSize: 11,
                          color: 'var(--oc-green)',
                          minWidth: 20,
                        }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Content */}
            <div className="oc-legal-content">
              {sections.map((s, i) => (
                <section
                  key={s.id}
                  id={s.id}
                  style={{
                    paddingTop: i === 0 ? 0 : 48,
                    paddingBottom: 48,
                    borderBottom: i === sections.length - 1 ? 'none' : '0.5px solid var(--oc-hairline)',
                    scrollMarginTop: 96, /* keep heading clear of sticky nav when anchor-jumping */
                  }}
                >
                  <div className="oc-eyebrow oc-mb-2">Section {String(i + 1).padStart(2, '0')}</div>
                  <h2 className="oc-h2" style={{ fontSize: 28, marginBottom: 20 }}>{s.title}</h2>
                  <div className="oc-legal-body">{s.body}</div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .oc-legal-body p { margin-bottom: 14px; font-size: 15px; line-height: 1.7; color: var(--oc-body); }
        .oc-legal-body p:last-child { margin-bottom: 0; }
        .oc-legal-body ul, .oc-legal-body ol { margin: 0 0 14px; padding-left: 22px; font-size: 15px; line-height: 1.7; color: var(--oc-body); }
        .oc-legal-body li { margin-bottom: 6px; }
        .oc-legal-body strong { font-weight: 500; color: var(--oc-ink); }
        .oc-legal-body h3 {
          font-family: var(--oc-font-display);
          font-weight: 400;
          font-size: 18px;
          color: var(--oc-ink);
          margin: 24px 0 10px;
          letter-spacing: -0.01em;
        }
        .oc-legal-body code {
          font-family: var(--oc-font-mono);
          font-size: 13px;
          background: var(--oc-paper-deep);
          padding: 2px 6px;
          border-radius: 4px;
        }
        @media (max-width: 900px) {
          .oc-legal-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .oc-legal-toc  { position: static !important; }
        }
      `}</style>
    </>
  );
}
